import { inject, Injectable } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import type {
  WorkflowNode,
  WorkflowEdge,
  WorkflowEdgeCondition,
  WorkflowGuard,
  WorkflowStepDelay,
  WorkflowStepAssign,
  WorkflowStepTransition,
  WorkflowStepView,
  WorkflowViewModel,
  WorkflowRunViewModel,
  WorkflowExecutionEvent,
} from './workflow.types';
import { START_NODE_ID, END_NODE_ID } from './workflow-block.converter';

export * from './workflow.types';
export { START_NODE_ID, END_NODE_ID } from './workflow-block.converter';

@Injectable({ providedIn: 'root' })
export class WorkflowService {
  private apiService = inject(EnrollhereApiService);
  private queryClient = inject(QueryClient);

  // --- Workflow CRUD ---

  async listWorkflows(
    agencyId: string,
    options?: { size?: number }
  ): Promise<{ workflows: WorkflowViewModel[]; total: number }> {
    const params = new URLSearchParams({ agencyId });
    if (options?.size) {
      params.set('size', String(options.size));
    }
    return this.apiService.get<{
      workflows: WorkflowViewModel[];
      total: number;
    }>(`workflow?${params.toString()}`);
  }

  /**
   * Aggregate workflow list across entitled agencies.
   * Backend: POST /v1/workflow/search
   */
  async searchWorkflows(request: {
    filter?: { agencyId?: string[] };
    limit?: number;
  }): Promise<{
    workflows: WorkflowViewModel[];
    total: number;
    limit: number;
  }> {
    return this.apiService.post<{
      workflows: WorkflowViewModel[];
      total: number;
      limit: number;
    }>('workflow/search', request);
  }

  async getWorkflow(
    agencyId: string,
    workflowId: string
  ): Promise<WorkflowViewModel> {
    const response = await this.apiService.get<{
      workflow: WorkflowViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}?agencyId=${encodeURIComponent(agencyId)}`
    );
    return response.workflow;
  }

  async createWorkflow(
    agencyId: string,
    payload: {
      name: string;
      description?: string;
      nodes: WorkflowNode[];
      edges: WorkflowEdge[];
      guards?: WorkflowGuard[];
      labels?: string[];
    }
  ): Promise<WorkflowViewModel> {
    const response = await this.apiService.post<{
      workflow: WorkflowViewModel;
    }>('workflow', { ...payload, agencyId });
    return response.workflow;
  }

  async updateWorkflow(
    agencyId: string,
    workflowId: string,
    payload: {
      name?: string;
      description?: string;
      nodes?: WorkflowNode[];
      edges?: WorkflowEdge[];
      guards?: WorkflowGuard[];
      labels?: string[];
    }
  ): Promise<WorkflowViewModel> {
    const response = await this.apiService.put<{
      workflow: WorkflowViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}?agencyId=${encodeURIComponent(agencyId)}`,
      payload
    );
    return response.workflow;
  }

  async deleteWorkflow(agencyId: string, workflowId: string): Promise<void> {
    await this.apiService.delete(
      `workflow/${encodeURIComponent(workflowId)}?agencyId=${encodeURIComponent(agencyId)}`
    );
  }

  async activateWorkflow(
    agencyId: string,
    workflowId: string
  ): Promise<WorkflowViewModel> {
    const response = await this.apiService.post<{
      workflow: WorkflowViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/activate?agencyId=${encodeURIComponent(agencyId)}`,
      {}
    );
    return response.workflow;
  }

  async deactivateWorkflow(
    agencyId: string,
    workflowId: string
  ): Promise<WorkflowViewModel> {
    const response = await this.apiService.post<{
      workflow: WorkflowViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/deactivate?agencyId=${encodeURIComponent(agencyId)}`,
      {}
    );
    return response.workflow;
  }

  async cloneWorkflow(
    agencyId: string,
    workflowId: string,
    name?: string
  ): Promise<WorkflowViewModel> {
    const response = await this.apiService.post<{
      workflow: WorkflowViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/clone?agencyId=${encodeURIComponent(agencyId)}`,
      { ...(name ? { name } : {}) }
    );
    return response.workflow;
  }

  // --- Version History ---

  async listVersions(
    agencyId: string,
    workflowId: string
  ): Promise<{
    versions: Array<{
      version: number;
      savedAt: string;
      savedBy: string;
      name: string;
    }>;
  }> {
    return this.apiService.get<{
      versions: Array<{
        version: number;
        savedAt: string;
        savedBy: string;
        name: string;
      }>;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/versions?agencyId=${encodeURIComponent(agencyId)}`
    );
  }

  async restoreVersion(
    agencyId: string,
    workflowId: string,
    version: number
  ): Promise<WorkflowViewModel> {
    const response = await this.apiService.post<{
      workflow: WorkflowViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/versions/${version}/restore?agencyId=${encodeURIComponent(agencyId)}`,
      {}
    );
    return response.workflow;
  }

  // --- Workflow Runs ---

  async startRun(
    agencyId: string,
    workflowId: string,
    payload: {
      contactId: string;
      contactType: string;
      contactName?: string;
      relations?: Array<{
        id: string;
        type: string | number;
        agencyId?: string;
        name?: string;
      }>;
      metadata?: Record<string, unknown>;
    }
  ): Promise<WorkflowRunViewModel> {
    const response = await this.apiService.post<{
      run: WorkflowRunViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/run?agencyId=${encodeURIComponent(agencyId)}`,
      payload
    );
    return response.run;
  }

  async getRun(
    agencyId: string,
    workflowId: string,
    runId: string
  ): Promise<WorkflowRunViewModel> {
    const response = await this.apiService.get<{
      run: WorkflowRunViewModel;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/run/${encodeURIComponent(runId)}?agencyId=${encodeURIComponent(agencyId)}`
    );
    return response.run;
  }

  async getRunLogs(
    agencyId: string,
    workflowId: string,
    runId: string
  ): Promise<WorkflowExecutionEvent[]> {
    const response = await this.apiService.get<{
      logs: WorkflowExecutionEvent[];
    }>(
      `workflow/${encodeURIComponent(workflowId)}/run/${encodeURIComponent(runId)}/logs?agencyId=${encodeURIComponent(agencyId)}`
    );
    return response.logs;
  }

  async getRunDefinition(
    agencyId: string,
    workflowId: string,
    runId: string
  ): Promise<Record<string, unknown>> {
    const response = await this.apiService.get<{
      definition: Record<string, unknown>;
    }>(
      `workflow/${encodeURIComponent(workflowId)}/run/${encodeURIComponent(runId)}/definition?agencyId=${encodeURIComponent(agencyId)}`
    );
    return response.definition;
  }

  async cancelRun(
    agencyId: string,
    workflowId: string,
    runId: string,
    reason?: string
  ): Promise<void> {
    await this.apiService.post(
      `workflow/${encodeURIComponent(workflowId)}/run/${encodeURIComponent(runId)}/cancel?agencyId=${encodeURIComponent(agencyId)}`,
      { reason }
    );
  }

  async searchRuns(
    filter: {
      agencyId?: string[];
      workflowId?: string[];
      contactId?: string[];
      contactType?: string[];
      relationIds?: string[];
      status?: string[];
    },
    size?: number,
    nextPageToken?: string
  ): Promise<{
    runs: WorkflowRunViewModel[];
    total: number;
    nextPageToken?: string;
  }> {
    return this.apiService.post<{
      runs: WorkflowRunViewModel[];
      total: number;
      nextPageToken?: string;
    }>('workflow/run/search', { filter, size, nextPageToken });
  }

  // --- Conversion helpers: steps <-> nodes ---

  nodesToSteps(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[]
  ): WorkflowStepView[] {
    const taskNodes = nodes
      .filter(
        n => n.type === 'WORKFLOW_NODE_TYPE_TASK' || n.type === 'stepNode'
      )
      .sort((a, b) => a.position.y - b.position.y);

    return taskNodes.map((node, index) => {
      const config = node.config || {};
      const outEdges = edges.filter(e => e.source === node.id && e.condition);

      const transitions: WorkflowStepTransition[] = outEdges
        .filter(e => e.condition?.value)
        .map(e => ({
          outcomeCode: e.condition!.value!,
          targetStepId: e.target === END_NODE_ID ? '_END_' : e.target,
        }));

      const delay: WorkflowStepDelay | undefined =
        (config['delay'] as WorkflowStepDelay | undefined) ||
        (config['delayMinutes']
          ? {
              type: 'duration',
              durationMinutes: config['delayMinutes'] as number,
            }
          : undefined);

      const assign: WorkflowStepAssign | undefined =
        (config['assign'] as WorkflowStepAssign | undefined) ||
        (config['assignedToUserId'] || config['assignedToTag']
          ? {
              strategy: 'user',
              userId: config['assignedToUserId'] as string | undefined,
              tag: config['assignedToTag'] as string | undefined,
            }
          : undefined);

      return {
        stepId: node.id,
        name: node.label,
        order: index,
        categoryId: (config['categoryId'] as string) || '',
        delay,
        assign,
        transitions,
      };
    });
  }

  stepsToNodes(
    steps: WorkflowStepView[],
    existingNodes?: WorkflowNode[],
    existingEdges?: WorkflowEdge[]
  ): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
    const nodes: WorkflowNode[] = [];
    const edges: WorkflowEdge[] = [];

    const nonTaskNodes = (existingNodes || []).filter(
      n =>
        n.type !== 'WORKFLOW_NODE_TYPE_TASK' &&
        n.type !== 'stepNode' &&
        n.type !== 'WORKFLOW_NODE_TYPE_START' &&
        n.type !== 'startNode' &&
        n.type !== 'WORKFLOW_NODE_TYPE_END' &&
        n.type !== 'endNode'
    );

    const nonTaskEdges = (existingEdges || []).filter(e => {
      const sourceIsTask =
        steps.some(s => s.stepId === e.source) || e.source === START_NODE_ID;
      const targetIsTask =
        steps.some(s => s.stepId === e.target) || e.target === END_NODE_ID;
      return !sourceIsTask && !targetIsTask;
    });

    nodes.push({
      id: START_NODE_ID,
      type: 'WORKFLOW_NODE_TYPE_START',
      label: 'Start',
      position: { x: 300, y: 0 },
    });

    nodes.push({
      id: END_NODE_ID,
      type: 'WORKFLOW_NODE_TYPE_END',
      label: 'End',
      position: { x: 300, y: (steps.length + 1) * 200 },
    });

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]!;
      nodes.push({
        id: step.stepId,
        type: 'WORKFLOW_NODE_TYPE_TASK',
        label: step.name,
        position: { x: 300, y: (i + 1) * 200 },
        config: {
          categoryId: step.categoryId,
          ...(step.delay ? { delay: step.delay } : {}),
          ...(step.assign ? { assign: step.assign } : {}),
          delayMinutes: step.delay?.durationMinutes || 0,
          assignedToUserId: step.assign?.userId,
          assignedToTag: step.assign?.tag,
        },
      });
    }

    if (steps.length > 0) {
      edges.push({
        id: `e-${START_NODE_ID}-${steps[0]!.stepId}`,
        source: START_NODE_ID,
        target: steps[0]!.stepId,
      });
    } else {
      edges.push({
        id: `e-${START_NODE_ID}-${END_NODE_ID}`,
        source: START_NODE_ID,
        target: END_NODE_ID,
      });
    }

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]!;
      const nextStep = steps[i + 1];
      const transitions = step.transitions || [];
      const hasWildcard = transitions.some(t => t.outcomeCode === '*');

      for (const t of transitions) {
        const targetId =
          t.targetStepId === '_END_' ? END_NODE_ID : t.targetStepId;
        edges.push({
          id: `e-${step.stepId}-${targetId}-${t.outcomeCode}`,
          source: step.stepId,
          target: targetId,
          label: t.outcomeCode === '*' ? 'Any' : t.outcomeCode,
          condition: {
            type: 'WORKFLOW_EDGE_CONDITION_TYPE_VALUE_MATCH',
            value: t.outcomeCode,
          },
        });
      }

      if (!hasWildcard) {
        const defaultTarget = nextStep ? nextStep.stepId : END_NODE_ID;
        edges.push({
          id: `e-${step.stepId}-${defaultTarget}-default`,
          source: step.stepId,
          target: defaultTarget,
        });
      }
    }

    nodes.push(...nonTaskNodes);
    edges.push(...nonTaskEdges);

    return { nodes, edges };
  }

  convertFlowNodes(
    flowNodes: Array<{
      id: string;
      type?: string;
      position?: { x: number; y: number };
      data?: Record<string, unknown>;
    }>,
    flowEdges: Array<{
      id: string;
      source: string;
      target: string;
      sourceHandle?: string;
      data?: Record<string, unknown>;
    }>
  ): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
    const nodeTypeMap: Record<string, string> = {
      startNode: 'WORKFLOW_NODE_TYPE_START',
      endNode: 'WORKFLOW_NODE_TYPE_END',
      exitNode: 'WORKFLOW_NODE_TYPE_END',
      stepNode: 'WORKFLOW_NODE_TYPE_TASK',
      conditionNode: 'WORKFLOW_NODE_TYPE_CONDITION',
      waitNode: 'WORKFLOW_NODE_TYPE_WAIT',
      waitEventNode: 'WORKFLOW_NODE_TYPE_WAIT_FOR_EVENT',
      triggerNode: 'WORKFLOW_NODE_TYPE_TRIGGER',
      actionSmsNode: 'WORKFLOW_NODE_TYPE_ACTION_SMS',
      aiSmsNode: 'WORKFLOW_NODE_TYPE_AI_SMS',
      actionEmailNode: 'WORKFLOW_NODE_TYPE_ACTION_EMAIL',
      actionNotifyNode: 'WORKFLOW_NODE_TYPE_ACTION_NOTIFICATION',
      actionUpdateLeadNode: 'WORKFLOW_NODE_TYPE_ACTION_UPDATE_LEAD',
      actionUpdatePolicyNode: 'WORKFLOW_NODE_TYPE_ACTION_UPDATE_POLICY',
      actionLeadListNode: 'WORKFLOW_NODE_TYPE_ACTION_LEAD_LIST',
      actionChannelConsentNode: 'WORKFLOW_NODE_TYPE_ACTION_CHANNEL_CONSENT',
      actionCheckChannelDncNode: 'WORKFLOW_NODE_TYPE_ACTION_CHECK_CHANNEL_DNC',
      actionResolveTransferLineageNode:
        'WORKFLOW_NODE_TYPE_ACTION_RESOLVE_TRANSFER_LINEAGE',
      actionChangePolicyOwnerNode:
        'WORKFLOW_NODE_TYPE_ACTION_CHANGE_POLICY_OWNER',
      actionUpdateCallDispoNode: 'WORKFLOW_NODE_TYPE_ACTION_UPDATE_CALL_DISPO',
      actionWebhookNode: 'WORKFLOW_NODE_TYPE_ACTION_WEBHOOK',
      actionCancelNode: 'WORKFLOW_NODE_TYPE_ACTION_CANCEL_WORKFLOW',
      actionStartNode: 'WORKFLOW_NODE_TYPE_ACTION_START_WORKFLOW',
      refreshDataNode: 'WORKFLOW_NODE_TYPE_REFRESH_DATA',
      switchNode: 'WORKFLOW_NODE_TYPE_CONDITION',
    };

    const configExcludeKeys = new Set([
      'label',
      'highlighted',
      'highlightStatus',
      'stepNumber',
      'totalSteps',
    ]);

    const nodes: WorkflowNode[] = flowNodes.map(n => {
      const data = n.data || {};
      const config: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(data)) {
        if (!configExcludeKeys.has(k) && v !== undefined && v !== '') {
          config[k] = v;
        }
      }

      if (n.type === 'switchNode') {
        config['_blockActionType'] = 'switch';
      }

      return {
        id: n.id,
        type: nodeTypeMap[n.type || ''] || n.type || 'UNKNOWN',
        label: (data['label'] as string) || '',
        position: n.position || { x: 0, y: 0 },
        config: Object.keys(config).length > 0 ? config : undefined,
      };
    });

    const edges: WorkflowEdge[] = flowEdges.map(e => {
      const edgeData = e.data || {};
      let condition: WorkflowEdgeCondition | undefined;

      const conditionType = edgeData['conditionType'] as string | undefined;
      if (conditionType === 'outcome') {
        condition = {
          type: 'WORKFLOW_EDGE_CONDITION_TYPE_VALUE_MATCH',
          value: (edgeData['conditionValue'] as string) || '',
        };
      } else if (conditionType === 'value') {
        condition = {
          type: 'WORKFLOW_EDGE_CONDITION_TYPE_VALUE_MATCH',
          field: (edgeData['field'] as string) || '',
          operator: (edgeData['operator'] as string) || '==',
          value: (edgeData['value'] as string) || '',
        };
      }

      return {
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        label: (edgeData['label'] as string) || undefined,
        condition,
        priority: edgeData['priority'] as number | undefined,
      };
    });

    return { nodes, edges };
  }

  convertNodesToFlow(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[]
  ): { nodes: unknown[]; edges: unknown[] } {
    const reverseTypeMap: Record<string, string> = {
      WORKFLOW_NODE_TYPE_START: 'startNode',
      WORKFLOW_NODE_TYPE_END: 'endNode',
      WORKFLOW_NODE_TYPE_TASK: 'stepNode',
      WORKFLOW_NODE_TYPE_CONDITION: 'conditionNode',
      WORKFLOW_NODE_TYPE_WAIT: 'waitNode',
      WORKFLOW_NODE_TYPE_WAIT_FOR_EVENT: 'waitEventNode',
      WORKFLOW_NODE_TYPE_TRIGGER: 'triggerNode',
      WORKFLOW_NODE_TYPE_ACTION_SMS: 'actionSmsNode',
      WORKFLOW_NODE_TYPE_AI_SMS: 'aiSmsNode',
      WORKFLOW_NODE_TYPE_ACTION_EMAIL: 'actionEmailNode',
      WORKFLOW_NODE_TYPE_ACTION_NOTIFICATION: 'actionNotifyNode',
      WORKFLOW_NODE_TYPE_ACTION_UPDATE_LEAD: 'actionUpdateLeadNode',
      WORKFLOW_NODE_TYPE_ACTION_UPDATE_POLICY: 'actionUpdatePolicyNode',
      WORKFLOW_NODE_TYPE_ACTION_LEAD_LIST: 'actionLeadListNode',
      WORKFLOW_NODE_TYPE_ACTION_CHANNEL_CONSENT: 'actionChannelConsentNode',
      WORKFLOW_NODE_TYPE_ACTION_CHECK_CHANNEL_DNC: 'actionCheckChannelDncNode',
      WORKFLOW_NODE_TYPE_ACTION_RESOLVE_TRANSFER_LINEAGE:
        'actionResolveTransferLineageNode',
      WORKFLOW_NODE_TYPE_ACTION_CHANGE_POLICY_OWNER:
        'actionChangePolicyOwnerNode',
      WORKFLOW_NODE_TYPE_ACTION_UPDATE_CALL_DISPO: 'actionUpdateCallDispoNode',
      WORKFLOW_NODE_TYPE_ACTION_WEBHOOK: 'actionWebhookNode',
      WORKFLOW_NODE_TYPE_ACTION_CANCEL_WORKFLOW: 'actionCancelNode',
      WORKFLOW_NODE_TYPE_ACTION_START_WORKFLOW: 'actionStartNode',
      WORKFLOW_NODE_TYPE_REFRESH_DATA: 'refreshDataNode',
    };

    const taskNodes = nodes.filter(n => n.type === 'WORKFLOW_NODE_TYPE_TASK');
    let stepCounter = 0;

    const flowNodes = nodes.map(n => {
      let flowType = reverseTypeMap[n.type] || n.type;

      if (n.type === 'WORKFLOW_NODE_TYPE_END' && n.id !== END_NODE_ID) {
        flowType = 'exitNode';
      }
      if (
        n.type === 'WORKFLOW_NODE_TYPE_CONDITION' &&
        n.config?.['_blockActionType'] === 'switch'
      ) {
        flowType = 'switchNode';
      }

      const data: Record<string, unknown> = { ...n.config };
      if (n.label) data['label'] = n.label;

      if (n.type === 'WORKFLOW_NODE_TYPE_TASK') {
        stepCounter++;
        data['stepNumber'] = stepCounter;
        data['totalSteps'] = taskNodes.length;
      }

      return { id: n.id, type: flowType, position: n.position, data };
    });

    const outcomeCodes = new Set<string>();
    for (const n of nodes) {
      const outcomes =
        (n.config?.['outcomes'] as Array<{ code: string }>) ?? [];
      for (const o of outcomes) outcomeCodes.add(o.code);
    }

    const flowEdges = edges.map(e => {
      const data: Record<string, unknown> = { edgeType: 'default' };
      let sourceHandle = e.sourceHandle || 'bottom';

      if (e.condition) {
        if (e.condition.field) {
          data['conditionType'] = 'value';
          data['field'] = e.condition.field;
          data['operator'] = e.condition.operator || '==';
          data['value'] = e.condition.value || '';
        } else if (e.condition.value) {
          data['conditionType'] = 'outcome';
          data['conditionValue'] = e.condition.value;
          sourceHandle = e.condition.value;
        }
      } else if (e.label && outcomeCodes.has(e.label)) {
        data['conditionType'] = 'outcome';
        data['conditionValue'] = e.label;
        sourceHandle = e.label;
      }

      if (e.label) data['label'] = e.label;
      if (e.priority !== undefined) data['priority'] = e.priority;

      return {
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle,
        targetHandle: 'top',
        type: 'transition',
        data,
      };
    });

    return { nodes: flowNodes, edges: flowEdges };
  }

  autoLayoutNodes(
    flowNodes: Array<{
      id: string;
      type?: string;
      position?: { x: number; y: number };
      [k: string]: unknown;
    }>,
    flowEdges: Array<{ source: string; target: string; [k: string]: unknown }>
  ): typeof flowNodes {
    if (flowNodes.length <= 2) return flowNodes;

    const nonTerminal = flowNodes.filter(
      n =>
        n.type !== 'startNode' && n.type !== 'endNode' && n.type !== 'exitNode'
    );
    if (nonTerminal.length === 0) return flowNodes;

    const yValues = nonTerminal.map(n => n.position?.y ?? 0);
    const yBuckets = new Map<number, number>();
    for (const y of yValues) {
      const key = Math.round(y / 10) * 10;
      yBuckets.set(key, (yBuckets.get(key) ?? 0) + 1);
    }
    const maxBucket = Math.max(...yBuckets.values());
    if (maxBucket < 3) return flowNodes;

    const adj = new Map<string, string[]>();
    for (const n of flowNodes) adj.set(n.id, []);
    for (const e of flowEdges) {
      adj.get(e.source)?.push(e.target);
    }

    const startNode = flowNodes.find(n => n.type === 'startNode');
    const startId = startNode?.id ?? flowNodes[0]!.id;

    const depth = new Map<string, number>();
    const queue: string[] = [startId];
    depth.set(startId, 0);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const d = depth.get(current)!;
      for (const child of adj.get(current) ?? []) {
        if (!depth.has(child)) {
          depth.set(child, d + 1);
          queue.push(child);
        }
      }
    }

    for (const n of flowNodes) {
      if (!depth.has(n.id)) depth.set(n.id, depth.size);
    }

    const levels = new Map<number, string[]>();
    for (const [id, d] of depth) {
      if (!levels.has(d)) levels.set(d, []);
      levels.get(d)!.push(id);
    }

    const newPositions = new Map<string, { x: number; y: number }>();
    for (const [d, ids] of levels) {
      const centerX = 300;
      const spacing = 250;
      const totalWidth = (ids.length - 1) * spacing;
      const startX = centerX - totalWidth / 2;

      for (let i = 0; i < ids.length; i++) {
        newPositions.set(ids[i]!, { x: startX + i * spacing, y: d * 200 });
      }
    }

    return flowNodes.map(n => ({
      ...n,
      position: newPositions.get(n.id) ?? n.position ?? { x: 0, y: 0 },
    }));
  }

  invalidateQueries() {
    this.queryClient.invalidateQueries({ queryKey: ['workflows'] });
    this.queryClient.invalidateQueries({ queryKey: ['workflow'] });
    this.queryClient.invalidateQueries({ queryKey: ['workflowRuns'] });
  }
}
